import React, { useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tooltip from '@mui/material/Tooltip';
import AriaLiveRegion from '../AriaLiveRegion';
import { validateIdNumber } from '../../utils/validators';
import { useTranslation } from 'react-i18next';

/**
 * Generic ID/Document Number Input Component
 * Supports any alphanumeric ID format (passports, national IDs, etc.)
 */
function IsraeliIdInput({ index, inputField, handleInputChange, handleMouseEnter, handleMouseLeave, hoveredIndex, windowWidth, pageHeight, pageWidth }) {
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const validateInput = (value) => {
    // Generic ID validation (5-20 alphanumeric characters)
    const validationError = validateIdNumber(value, 5, 20, t);
    return validationError || '';
  };

  function handleLocalInputChange(event, index) {
    const value = event.target.value;
    const validationError = validateInput(value);
    setError(validationError); // Set the error message

    // Call the parent's input change handler
    handleInputChange(event, index);
  }

  const hasError = !inputField.valid || !!error;
  const isSuccess = inputField.valid && inputField.value && inputField.value.trim() !== '' && !error;
  const errorId = hasError ? `error-${inputField.editor.inputType.value}-${index}` : undefined;
  const fieldLabel = inputField.editor.inputType.label || inputField.editor.inputType.value;

  // Combined error message for screen readers
  const errorMessage = error || (hasError ? `שדה מספר זהות ${fieldLabel} דורש תיקון` : '');

  // CRITICAL FIX: Calculate rendered PDF height correctly
  const renderedHeight = windowWidth * (pageHeight / pageWidth);
  const fieldHeight = windowWidth * inputField.editor.height / 40;

  return (
    <>
      <input
        key={index}
        type="text"
        value={inputField.value}
        onChange={(event) => handleLocalInputChange(event, index)}
        aria-label={fieldLabel}
        aria-invalid={hasError}
        aria-describedby={errorId}
        aria-required={inputField.mandatory ? 'true' : 'false'}
        style={{
          opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
          position: 'absolute',
          top: inputField.y * renderedHeight,
          left: inputField.x * windowWidth,
          width: `${(windowWidth * inputField.editor.width / 1.5)}px`,
          height: `${fieldHeight}px`,
          fontSize: `${windowWidth / 60}px`,
          padding: '4px',
          border: hasError ? '2px solid #ef4444' : isSuccess ? '2px solid #22c55e' : '1px solid #e5e7eb',
          borderRadius: '4px',
          transition: 'all 0.2s ease',
          boxShadow: hasError ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : isSuccess ? '0 0 0 3px rgba(34, 197, 94, 0.1)' : 'none',
        }}
        onMouseEnter={(event) => handleMouseEnter(event, index)}
        onMouseLeave={handleMouseLeave}
      />
      {/* Validation error message */}
      {error && (
        <div
          id={errorId}
          style={{
            position: 'absolute',
            top: inputField.y * renderedHeight + fieldHeight + 4,
            left: inputField.x * windowWidth,
            fontSize: `${Math.max(windowWidth / 100, 11)}px`,
            color: '#ef4444',
            fontWeight: '500',
            pointerEvents: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '2px 6px',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            maxWidth: `${(windowWidth * inputField.editor.width / 1.5)}px`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          ⚠️ {error}
        </div>
      )}
      {/* Success checkmark */}
      {isSuccess && (
        <div
          style={{
            position: 'absolute',
            top: inputField.y * renderedHeight,
            left: inputField.x * windowWidth + (windowWidth * inputField.editor.width / 1.5) + 4,
            fontSize: `${windowWidth / 60}px`,
            color: '#22c55e',
            fontWeight: 'bold',
            pointerEvents: 'none',
          }}
        >
          ✓
        </div>
      )}
      {hasError && (
        <AriaLiveRegion
          id={errorId}
          message={errorMessage}
          type="error"
          visible={false}
          sx={{
            position: 'absolute',
            top: inputField.y * renderedHeight + fieldHeight + 2,
            left: inputField.x * windowWidth,
            fontSize: `${windowWidth / 80}px`,
            color: '#ef4444',
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
}

export default IsraeliIdInput;
